import * as React from 'react';
import { useState } from 'react';
import { 
  ChevronRight, 
  ChevronDown, 
  Plus, 
  Edit2, 
  Move, 
  Trash2, 
  AlertCircle,
  Phone
} from 'lucide-react';
import { z } from 'zod';
import { UuidSchema } from '@repo/validation';
import { useFormModal } from '@repo/form';
import { FormModal, DynamicContactList, DynamicAddressList, type ContactItem, type AddressItem } from '@repo/web-form';

import { cn } from '@repo/utils';
import { Button } from '../../../components/ui/Button';
import { FormField, Input } from '../../../components/ui/FormField';
import { FormSelect } from '../../../components/ui/FormSelect';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from '../../../components/ui/Dialog';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import { OrgUnitPicker } from './OrgUnitPicker';
import { 
  useOrgUnits, 
  useCreateOrgUnit, 
  useUpdateOrgUnit, 
  useReparentOrgUnit, 
  useDeactivateOrgUnit,
  type OrgUnitNode,
  type OrgUnitType 
} from '../network/org.api';

const ORG_UNIT_TYPES: { value: OrgUnitType; label: string }[] = [
  { value: 'HEAD_OFFICE', label: 'Head Office' },
  { value: 'BRANCH', label: 'Branch' },
];

const orgUnitSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  code: z.string().min(1, 'Code is required').max(20),
  unitType: z.enum(['HEAD_OFFICE', 'BRANCH']),
  phone: z.string().optional().nullable(),
  address: z.record(z.string(), z.unknown()).optional().nullable(),
  parentId: UuidSchema.optional().nullable(),
});

const validate = (schema: z.ZodTypeAny, value: unknown) => {
  const res = schema.safeParse(value);
  return res.success ? undefined : res.error.issues[0].message;
};

interface FormFieldRenderProps<T> {
  state: {
    value: T;
    meta: unknown;
  };
  handleChange: (val: T) => void;
  handleBlur?: () => void;
}

export function OrgUnitTree() {
  const { data: nodes, isLoading } = useOrgUnits();
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set());

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (isLoading) return <div>Loading branches...</div>;
  if (!nodes || nodes.length === 0) return <div>No branches found. <AddNodeButton parentId={null} allNodes={[]} /></div>;

  const headOffice = nodes.find((n) => n.unitType === 'HEAD_OFFICE') ?? nodes[0];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-sm font-medium text-text-muted uppercase tracking-wider">Hierarchy</h2>
        <AddNodeButton parentId={headOffice ? headOffice.id : null} allNodes={nodes} />
      </div>
      <div className="border border-border rounded-[14px] bg-surface-2 overflow-hidden">
        {nodes.map((node) => (
          <TreeNode 
            key={node.id} 
            node={node} 
            level={0} 
            expandedIds={expandedIds} 
            onToggle={toggleExpand} 
            allNodes={nodes}
          />
        ))}
      </div>
    </div>
  );
}

function TreeNode({ 
  node, 
  level, 
  expandedIds, 
  onToggle,
  allNodes 
}: { 
  node: OrgUnitNode; 
  level: number; 
  expandedIds: Set<string>; 
  onToggle: (id: string) => void;
  allNodes: OrgUnitNode[];
}) {
  const isExpanded = expandedIds.has(node.id);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div className="flex flex-col">
      <div 
        className={cn(
          "group flex items-center gap-2 py-3 px-4 hover:bg-surface-3 transition-colors border-b border-border last:border-0",
          !node.isActive && "opacity-60"
        )}
        style={{ paddingLeft: `${(level * 24) + 16}px` }}
      >
        <Button 
          variant="ghost"
          onClick={() => onToggle(node.id)}
          className={cn(
            "p-1 hover:bg-surface-4 rounded transition-colors bg-transparent hover:bg-transparent",
            !hasChildren && "invisible"
          )}
        >
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </Button>

        <div className="flex-1 flex items-center gap-3">
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-text-primary">{node.name}</span>
              <span className="text-caption font-mono bg-surface-4 px-1.5 py-0.5 rounded text-text-muted border border-border">
                {node.code}
              </span>
              <span className={cn(
                "text-caption font-semibold px-1.5 py-0.5 rounded uppercase tracking-tight",
                node.unitType === 'BRANCH' ? "bg-accent-500/10 text-accent-500" : "bg-surface-4 text-text-muted"
              )}>
                {node.unitType}
              </span>
              {!node.isActive && (
                <span className="text-caption font-semibold px-1.5 py-0.5 rounded bg-danger-500/10 text-danger-text uppercase">
                  Inactive
                </span>
              )}
            </div>
            {node.phone && (
              <div className="flex items-center gap-1 mt-1 text-xs text-text-muted">
                <Phone className="h-3 w-3" />
                <span>{node.phone}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <AddNodeButton parentId={node.id} allNodes={allNodes} />
          <EditNodeButton node={node} />
          <MoveNodeButton node={node} allNodes={allNodes} />
          <DeactivateNodeButton node={node} />
        </div>
      </div>

      {isExpanded && hasChildren && (
        <div className="flex flex-col">
          {node.children.map((child) => (
            <TreeNode 
              key={child.id} 
              node={child} 
              level={level + 1} 
              expandedIds={expandedIds} 
              onToggle={onToggle}
              allNodes={allNodes}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AddNodeButton({ parentId, allNodes = [] }: { parentId: string | null; allNodes?: OrgUnitNode[] }) {
  const [open, setOpen] = React.useState(false);
  const mutation = useCreateOrgUnit();
  const [contacts, setContacts] = useState<ContactItem[]>([
    { type: 'PHONE', label: 'BRANCH_LANDLINE', value: '', isPrimary: true },
  ]);
  const [addresses, setAddresses] = useState<AddressItem[]>([]);

  const headExists = allNodes.some((n) => n.unitType === 'HEAD_OFFICE');
  const availableTypes = headExists
    ? ORG_UNIT_TYPES.filter((t) => t.value !== 'HEAD_OFFICE')
    : ORG_UNIT_TYPES;
  const defaultUnitType: OrgUnitType = (headExists || parentId) ? 'BRANCH' : 'HEAD_OFFICE';

  const modal = useFormModal(
    {
      defaultValues: {
        name: '',
        code: '',
        unitType: defaultUnitType,
        phone: '',
        parentId: parentId,
        address: null as never,
        headUserId: null as never,
      },
      schema: orgUnitSchema as never,
    },
    mutation,
    {
      onBeforeSubmit: (values) => {
        const primaryPhone = contacts.find((c) => c.type === 'PHONE' && c.isPrimary)?.value || contacts[0]?.value || '';
        return {
          ...values,
          phone: primaryPhone,
          contacts,
          addresses,
        };
      },
      onSuccess: () => {
        setOpen(false);
      },
    },
  );

  return (
    <>
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={() => setOpen(true)}
        className="h-8 w-8 p-0"
        title="Add child unit"
      >
        <Plus className="h-4 w-4" />
      </Button>

      <FormModal open={open} onClose={() => setOpen(false)} title="Add Organizational Unit" modal={modal} submitLabel="Create Unit">
        <modal.form.Field
          name="name"
          validators={{ onChange: ({ value }: { value: string }) => validate(orgUnitSchema.shape.name, value) }}
          children={(field: FormFieldRenderProps<string>) => (
            <FormField label="Name" required error={modal.fieldError(field.state.meta as never)}>
              <Input 
                value={field.state.value} 
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                placeholder="e.g. Chennai Central"
                disabled={modal.isSubmitting}
              />
            </FormField>
          )}
        />
        <div className="grid grid-cols-2 gap-4">
          <modal.form.Field
            name="code"
            validators={{ onChange: ({ value }: { value: string }) => validate(orgUnitSchema.shape.code, value) }}
            children={(field: FormFieldRenderProps<string>) => (
              <FormField label="Code" required error={modal.fieldError(field.state.meta as never)}>
                <Input 
                  value={field.state.value} 
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value.toUpperCase())}
                  placeholder="CHN-001"
                  maxLength={20}
                  disabled={modal.isSubmitting}
                />
              </FormField>
            )}
          />
          <modal.form.Field
            name="unitType"
            validators={{ onChange: ({ value }: { value: OrgUnitType }) => validate(orgUnitSchema.shape.unitType, value) }}
            children={(field: FormFieldRenderProps<OrgUnitType>) => (
              <FormSelect
                label="Unit Type"
                required
                options={availableTypes}
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => field.handleChange(e.target.value as OrgUnitType)}
                error={modal.fieldError(field.state.meta as never)}
                disabled={modal.isSubmitting || availableTypes.length <= 1}
              />
            )}
          />
        </div>

        <div className="border-t pt-4 space-y-4">
          <DynamicContactList
            contacts={contacts}
            disabled={modal.isSubmitting}
            onChange={(newContacts) => setContacts(newContacts)}
          />
          <DynamicAddressList
            addresses={addresses}
            disabled={modal.isSubmitting}
            onChange={(newAddrs) => setAddresses(newAddrs)}
          />
        </div>
      </FormModal>
    </>
  );
}

function EditNodeButton({ node }: { node: OrgUnitNode }) {
  const [open, setOpen] = React.useState(false);
  const mutation = useUpdateOrgUnit();
  const [contacts, setContacts] = useState<ContactItem[]>([
    { type: 'PHONE', label: 'BRANCH_LANDLINE', value: node.phone || '', isPrimary: true },
  ]);

  const modal = useFormModal(
    {
      defaultValues: {
        name: node.name,
        code: node.code,
        unitType: node.unitType,
        phone: node.phone ?? '',
      },
      schema: orgUnitSchema.pick({ name: true, code: true, unitType: true, phone: true }) as never,
    },
    mutation,
    {
      onBeforeSubmit: (value) => {
        const primaryPhone = contacts.find((c) => c.type === 'PHONE' && c.isPrimary)?.value || contacts[0]?.value || '';
        return {
          id: node.id,
          body: {
            ...value,
            phone: primaryPhone,
            contacts,
          } as never,
        };
      },
      onSuccess: () => {
        setOpen(false);
      },
    },
  );

  return (
    <>
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={() => setOpen(true)}
        className="h-8 w-8 p-0"
        title="Edit unit"
      >
        <Edit2 className="h-3.5 w-3.5" />
      </Button>

      <FormModal open={open} onClose={() => setOpen(false)} title="Edit Organizational Unit" modal={modal} submitLabel="Save Changes">
        <modal.form.Field
          name="name"
          validators={{ onChange: ({ value }: { value: string }) => validate(orgUnitSchema.shape.name, value) }}
          children={(field: FormFieldRenderProps<string>) => (
            <FormField label="Name" required error={modal.fieldError(field.state.meta as never)}>
              <Input 
                value={field.state.value} 
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                disabled={modal.isSubmitting}
              />
            </FormField>
          )}
        />
        <div className="grid grid-cols-2 gap-4">
          <modal.form.Field
            name="code"
            validators={{ onChange: ({ value }: { value: string }) => validate(orgUnitSchema.shape.code, value) }}
            children={(field: FormFieldRenderProps<string>) => (
              <FormField label="Code" required error={modal.fieldError(field.state.meta as never)}>
                <Input 
                  value={field.state.value} 
                  disabled
                  className="bg-surface-4"
                />
              </FormField>
            )}
          />
          <modal.form.Field
            name="unitType"
            validators={{ onChange: ({ value }: { value: OrgUnitType }) => validate(orgUnitSchema.shape.unitType, value) }}
            children={(field: FormFieldRenderProps<OrgUnitType>) => (
              <FormSelect
                label="Unit Type"
                required
                options={ORG_UNIT_TYPES}
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => field.handleChange(e.target.value as OrgUnitType)}
                error={modal.fieldError(field.state.meta as never)}
                disabled={modal.isSubmitting}
              />
            )}
          />
        </div>

        <div className="border-t pt-4">
          <DynamicContactList
            contacts={contacts}
            disabled={modal.isSubmitting}
            onChange={(newContacts) => setContacts(newContacts)}
          />
        </div>
      </FormModal>
    </>
  );
}

function MoveNodeButton({ node, allNodes }: { node: OrgUnitNode, allNodes: OrgUnitNode[] }) {
  const [open, setOpen] = React.useState(false);
  const [newParentId, setNewParentId] = React.useState<string | null>(node.parentId);
  const mutation = useReparentOrgUnit();

  const handleMove = async () => {
    await mutation.mutateAsync({ id: node.id, parentId: newParentId });
    setOpen(false);
  };

  return (
    <>
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={() => setOpen(true)}
        className="h-8 w-8 p-0"
        title="Move unit"
      >
        <Move className="h-3.5 w-3.5" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move Organizational Unit</DialogTitle>
            <DialogDescription>
              Select a new parent for <span className="font-semibold">{node.name}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-6">
            <OrgUnitPicker 
              nodes={allNodes}
              value={newParentId ?? ''}
              onChange={(val) => setNewParentId(val || null)}
              excludeSubtreeOfId={node.id}
              placeholder="Select new parent..."
              disabled={mutation.isPending}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleMove} 
              loading={mutation.isPending}
              disabled={newParentId === node.parentId || mutation.isPending}
            >
              Confirm Move
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function DeactivateNodeButton({ node }: { node: OrgUnitNode }) {
  const [open, setOpen] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const mutation = useDeactivateOrgUnit();

  const handleDeactivate = async () => {
    try {
      setErrorMsg(null);
      await mutation.mutateAsync(node.id);
      setOpen(false);
    } catch (err: unknown) {
      console.error('Failed to deactivate organizational unit', err);
      const error = err as { code?: string; message?: string };
      if (error.code === 'ORG_UNIT_HAS_ACTIVE_SCHEMES') {
        setErrorMsg(error.message || 'Cannot deactivate: This branch has active chit schemes.');
      } else {
        setErrorMsg('Failed to deactivate organizational unit. Please check dependencies and try again.');
      }
    }
  };

  return (
    <>
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={() => setOpen(true)}
        className="h-8 w-8 p-0 text-danger-text hover:text-danger-text hover:bg-danger-500/10"
        title="Deactivate unit"
        disabled={!node.isActive}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>

      <ConfirmDialog
        open={open}
        title="Deactivate Unit?"
        description={`Are you sure you want to deactivate "${node.name}"? Sub-units will remain but show a warning.`}
        confirmLabel="Deactivate"
        onConfirm={handleDeactivate}
        onCancel={() => { setOpen(false); setErrorMsg(null); }}
        loading={mutation.isPending}
      />

      {errorMsg && (
        <Dialog open={!!errorMsg} onOpenChange={() => setErrorMsg(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-danger-500/10">
                <AlertCircle className="h-6 w-6 text-danger-text" />
              </div>
              <DialogTitle className="text-center">Action Blocked</DialogTitle>
              <DialogDescription className="text-center pt-2">
                {errorMsg}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button className="w-full" onClick={() => setErrorMsg(null)}>Understood</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
